import { useState } from "react";
import { loginWithCredentials } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";
import { getRememberedLoginId } from "@/lib/api/token-store";

/**
 * Shared credentials-form state + submit flow, extracted from LoginPanel so the
 * CredentialsDialog (profile switcher "Add account" / no-PIN switch) reuses the exact same
 * behavior, including the LoginErrorDialog error mapping (credentials vs connection).
 *
 * onSuccess receives (result, { password }): the just-typed password is handed over in memory
 * only so login-owning components can offer PIN setup (which requires password re-auth)
 * without asking the user to retype it. The hook clears its own password state on success.
 */
export function useLoginForm({ initialLoginId, onSuccess } = {}) {
  const [loginId, setLoginId] = useState(() =>
    initialLoginId != null ? String(initialLoginId) : getRememberedLoginId(),
  );
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorDialog, setErrorDialog] = useState({ open: false, kind: "credentials" });

  const submit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await loginWithCredentials({ loginId, password });
      setLoginId(result.loginId);
      const submittedPassword = password;
      setPassword("");
      onSuccess?.(result, { password: submittedPassword });
    } catch (error) {
      console.error("[Auth] login request failed:", error);
      const kind = error instanceof ApiError ? "credentials" : "connection";
      setErrorDialog({ open: true, kind });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    loginId,
    setLoginId,
    password,
    setPassword,
    isSubmitting,
    errorDialog,
    setErrorDialog,
    submit,
  };
}
